import { Button, Group, LoadingOverlay, Modal, Radio, Select, Space, Stack } from '@mantine/core';
import { useField } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconWalletOff } from '@tabler/icons-react';
import { useCancelPaymentPlan, useGetActivities, useGetPaymentPlan, useGetTemplates } from 'api';
import type { Id, StepTypeParameter } from 'api/models';
import InvoiceTimeline from 'app/Private/Invoices/Invoice/InvoiceTimeline/InvoiceTimeline';
import classNames from 'classnames/bind';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import AttachedInvoicesList from 'components/shared/AttachedInvoices';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { omit } from 'lodash-es';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { isNullish } from 'remeda';
import type { AttachedInvoice, AttachedInvoices } from 'shared/hooks/schemas/attached-invoices';
import { formatActivities } from 'shared/utils/activities.utils';
import { formatCommunication } from 'shared/utils/mask.utils';
import { DialogShowId, dialogHide } from 'store/view/view.actions';
import type { CurrencyLocalizedMoneyObject } from 'types/currency';
import { computeNextStepDate, computeStatus } from '../PaymentPlan.utils';
import styleIdentifiers from './PaymentPlanDetail.module.scss';
import { PaymentPlanDetailTable } from './PaymentPlanDetailTable';

const styles = classNames.bind(styleIdentifiers);

type PaymentPlanDetailProps = {
  planId?: Id;
  inModal?: boolean;
};

export const PaymentPlanDetail = ({ planId, inModal }: PaymentPlanDetailProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const history = useHistory();

  const { id: paymentPlanId } = useParams<{ id: string }>();

  // Cancellation
  const [isModalOpened, modalHandler] = useDisclosure(false);
  const [shouldUnpause, setShouldUnpause] = useState<'pause' | 'unpause'>('pause');
  const selectedCancellationTemplateId = useField<string | null>({ initialValue: null });
  const { mutate: cancelPaymentPlan, isPending: isCancelPlanLoading } = useCancelPaymentPlan();
  const { data: cancellationTemplatesResponse } = useGetTemplates({
    plan_type: 'payment',
    step_type: 'plan_cancel' as StepTypeParameter,
  });
  const templates = (cancellationTemplatesResponse?.data ?? []).map(({ id, attributes }) => ({
    value: String(id),
    label: attributes.name,
  }));

  const {
    data: paymentPlanResponse,
    isLoading,
    isError,
    refetch,
  } = useGetPaymentPlan(planId || paymentPlanId);

  const {
    data: activitiesListResponse,
    isLoading: areActivitiesLoading,
    isError: isActivitiesError,
  } = useGetActivities({
    payment_plan_id: planId || paymentPlanId,
  });

  if (isLoading || !paymentPlanResponse || areActivitiesLoading) {
    return <LoadingOverlay visible />;
  }

  if (isError || isActivitiesError) {
    return <span>{t(i18nKeys.ERROR.ERROR)}</span>;
  }

  const { data: paymentPlan } = paymentPlanResponse;
  const canUseAdvancedPaymentPlans = company.package.can_use_advanced_payment_plans;

  const {
    id,
    attributes: {
      created_at,
      currency,
      communication,
      completed: isCompleted,
      cancelled: isCancelled,
      cancelled_at,
      debtor,
      invoices,
      payment_steps_attributes,
      workflow,
      with_fees: shouldIncludeFees,
      current_step,
      credit_notes,
      accounting_payments,
      third_party_payments,
    },
  } = paymentPlan;

  const cancelAndRedirect = () => {
    cancelPaymentPlan(
      {
        id,
        data: {
          unpause: shouldUnpause === 'unpause',
          template_id: selectedCancellationTemplateId.getValue()!,
        },
      },
      {
        onSuccess: () => {
          modals.closeAll();
          history.push('/payment-plans');
        },
      },
    );
  };

  const handleClickEditPlan = () => {
    dialogHide(DialogShowId.INVOICE);
    history.push(`/payment-plans/${id}/edit`);
  };

  const currentStep = payment_steps_attributes.find(step => step.id === current_step?.toString());

  const invoicesFormated = {
    type: 'attached_invoice',
    ...omit(invoices, ['details']),
    details: invoices!.details.map(
      ({
        id: invoiceId,
        attributes: { due_date: due_date_invoice, localized_money_object, ...rest },
      }) =>
        ({
          id: Number(invoiceId),
          due_date: dayjs(due_date_invoice),
          localized_money_object: localized_money_object as unknown as CurrencyLocalizedMoneyObject,
          ...rest,
        }) as unknown as AttachedInvoice,
    ),
  } as AttachedInvoices;

  const hasPlanEnded = isCancelled || isCompleted;
  const STATUS = computeStatus(isCancelled, isCompleted);
  const activitiesListFormatted = formatActivities(activitiesListResponse);

  return (
    <div className={styles('payment-plan-detail', inModal && 'in-modal')}>
      <Modal
        title={t(i18nKeys.PAYMENT_PLAN.CANCELLATION.TITLE)}
        opened={isModalOpened}
        onClose={modalHandler.close}
        size="500px"
      >
        <Stack gap="lg">
          <Radio.Group
            value={shouldUnpause}
            onChange={val => setShouldUnpause(val as 'pause' | 'unpause')}
          >
            <Group w="100%">
              <Radio
                flex={1}
                label={t(i18nKeys.PAYMENT_PLAN.CANCELLATION.KEEP_INVOICES_PAUSED)}
                value="pause"
              />
              <Radio
                flex={1}
                label={t(i18nKeys.PAYMENT_PLAN.CANCELLATION.RESUME_WORKFLOW)}
                value="unpause"
              />
            </Group>
          </Radio.Group>
          <Select
            {...selectedCancellationTemplateId.getInputProps()}
            label={t(i18nKeys.PAYMENT_PLAN.CANCELLATION.TEMPLATE_LABEL)}
            data={templates}
            allowDeselect={false}
          />
          <Group justify="right" mt="md">
            <Button variant="light" color="orange" onClick={modalHandler.close}>
              {t(i18nKeys.CANCEL)}
            </Button>
            <Button
              disabled={isNullish(selectedCancellationTemplateId.getValue())}
              color="red"
              loading={isCancelPlanLoading}
              leftSection={<IconWalletOff stroke={1.5} size={18} />}
              onClick={cancelAndRedirect}
            >
              {t(i18nKeys.PAYMENT_PLAN.CANCELLATION.CONFIRM)}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <PageTitle>
        {!inModal && <PageTitle.Back />}
        {`${t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)} #${id}`}
        <PageTitle.Actions>
          <Button
            onClick={handleClickEditPlan}
            disabled={!canUseAdvancedPaymentPlans || hasPlanEnded}
          >
            {t(i18nKeys.EDIT)}
          </Button>
          <Button color="red" onClick={modalHandler.open} disabled={hasPlanEnded}>
            {t(i18nKeys.CANCEL)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Stack>
        <div className={styles('line-wrapper')}>
          <Panel withBorder title={t(i18nKeys.CLIENT.GENERAL_INFO)}>
            <div>
              <div className={styles('item')}>
                <span className={styles('subtitle')}>{t(i18nKeys.CREATION_DATE)}</span>
                <span className={styles('value')}>{dayjs(created_at).format('L')}</span>
              </div>
              <div className={styles('item')}>
                <span className={styles('subtitle')}>
                  {t(i18nKeys.PAYMENT_PLAN.WORKFLOW_LABEL)}
                </span>
                <span className={styles('value')}>{workflow.attributes.name}</span>
              </div>
              <div className={styles('item')}>
                <span className={styles('subtitle')}>
                  {t(i18nKeys.PAYMENT_PLAN.ATTRIBUTES.COMMUNICATION)}
                </span>
                <span className={styles('value')}>{formatCommunication(communication)}</span>
              </div>
              <div className={styles('item')}>
                <span className={styles('subtitle')}>{t(i18nKeys.WITH_FEES)}</span>
                <span className={styles('value')}>
                  {t(i18nKeys[shouldIncludeFees ? 'YES' : 'NO'])}
                </span>
              </div>
            </div>
          </Panel>
          <Panel withBorder title={t(i18nKeys.DETAILS)}>
            <div className={styles('item')}>
              <span className={styles('subtitle')}>{t(i18nKeys.COMMON.CLIENT)}</span>
              <span className={styles('value', 'bigger')}>
                <ClientHoverCard
                  debtorId={String(debtor.id)}
                  debtorName={debtor.attributes.full_name}
                />
              </span>
            </div>
            <div className={styles('item')}>
              <span className={styles('subtitle')}>{t(i18nKeys.STATUS)}</span>
              <span className={styles('value')}>{t(i18nKeys[STATUS])}</span>
            </div>
            {isCancelled && (
              <div className={styles('item')}>
                <span className={styles('subtitle')}>{t(i18nKeys.CANCELLED_AT)}</span>
                <span className={styles('value')}>{dayjs(cancelled_at).format('L')}</span>
              </div>
            )}
            <div className={styles('item')}>
              <span className={styles('subtitle')}>{t(i18nKeys.NEXT_DEADLINE)}</span>
              <span className={styles('value')}>
                {computeNextStepDate(STATUS, currentStep) === '-'
                  ? t(i18nKeys.PAYMENT_PLAN.UTILS.NO_NEXT_STEP)
                  : computeNextStepDate(STATUS, currentStep)}
              </span>
            </div>
          </Panel>
        </div>
        <AttachedInvoicesList
          invoices={invoicesFormated}
          withoutFees={!shouldIncludeFees}
          creditNotes={credit_notes}
          payments={[...accounting_payments, ...third_party_payments]}
        />
        <PaymentPlanDetailTable
          currency={currency}
          paymentSteps={payment_steps_attributes}
          currentStep={current_step as number}
          refetchPaymentPlan={refetch}
        />
        {!!activitiesListFormatted.activities.length && (
          <InvoiceTimeline
            metadata={activitiesListFormatted.metadata}
            activities={activitiesListFormatted.activities}
          />
        )}
        <Space h="md" />
      </Stack>
    </div>
  );
};
